import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Box,
    Button,
    Center,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Badge
} from "@chakra-ui/react";
import { CheckCircleIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import moment from "moment";

export const CourseInterestReceived = () => {
    const { registrationID } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [registrationData, setRegistrationData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRegistration = async () => {
            setLoading(true);
            try {
                // First try to get the registration details
                const { data: registrationData, error: regError } = await supabase
                    .from("registration")
                    .select("*")
                    .eq("id", registrationID)
                    .single();

                if (regError) throw regError;

                // Check if this is a full certification registration
                // Note: registration table may not have full_certification_id directly
                // It might be stored in draft_cert_run_code or accessed via views
                if (registrationData.draft_cert_run_code) {
                    // Get the full certification run and then the certification
                    const { data: certRunData, error: certRunError } = await supabase
                        .from("full_certification_runs")
                        .select(`
                            id,
                            full_certification (
                                code,
                                name
                            )
                        `)
                        .eq("id", registrationData.draft_cert_run_code)
                        .single();
                    
                    if (!certRunError && certRunData?.full_certification) {
                        registrationData.full_certification_name = certRunData.full_certification.name;
                    }
                } else if (registrationData.full_certification_id) {
                    // Fallback: if full_certification_id exists (from a view), use code instead of id
                    const { data: certData, error: certError } = await supabase
                        .from("full_certification")
                        .select("name")
                        .eq("code", registrationData.full_certification_id)
                        .single();

                    if (!certError && certData) {
                        registrationData.full_certification_name = certData.name;
                    }
                }

                setRegistrationData(registrationData);
            } catch (error) {
                console.error("Error fetching registration:", error);
                toast({
                    title: "Error",
                    description: "Could not load registration details.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRegistration();
    }, [registrationID, toast]);

    if (loading) {
        return (
            <Center minH="100vh" bg="gray.50">
                <Spinner size="xl" color="teal.500" />
            </Center>
        );
    }

    if (!registrationData) {
        return (
            <Center minH="100vh" bg="gray.50">
                <Text color="gray.600">Registration not found.</Text>
            </Center>
        );
    }

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="container.md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Box
                        bg="white"
                        p={8}
                        borderRadius="lg"
                        boxShadow="md"
                        textAlign="center"
                    >
                        <VStack spacing={6}>
                            <Icon
                                as={CheckCircleIcon}
                                w={20}
                                h={20}
                                color="green.500"
                            />
                            
                            <Heading size="md" color="gray.800">
                                Registration Received
                            </Heading>

                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <Text fontSize="md">
                                    Thank you for your interest in this course! We'll contact you when your registration is confirmed.
                                </Text>
                            </Alert>

                            <Box
                                p={6}
                                borderWidth="1px"
                                borderRadius="md"
                                borderColor="gray.200"
                                bg="gray.50"
                                w="100%"
                            >
                                <VStack spacing={4} align="stretch">
                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            {registrationData.full_certification_name ? 'Program:' : 'Course:'}
                                        </Text>
                                        <Text color="gray.800">
                                            {registrationData.full_certification_name || registrationData.name}
                                        </Text>
                                    </HStack>

                                    {!registrationData.full_certification_name && (
                                        <HStack justify="space-between">
                                            <Text fontWeight="semibold" color="gray.700">
                                                Registration ID:
                                            </Text>
                                            <Badge colorScheme="blue" variant="subtle">
                                                {registrationData.id}
                                            </Badge>
                                        </HStack>
                                    )}


                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Participant:
                                        </Text>
                                        <Text color="gray.800">
                                            {registrationData.participant_name}
                                        </Text>
                                    </HStack>

                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Email:
                                        </Text>
                                        <Text color="gray.800">
                                            {registrationData.participant_email}
                                        </Text>
                                    </HStack>

                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Registration Type:
                                        </Text>
                                        <Badge colorScheme="purple" variant="subtle">
                                            {registrationData.registration_type}
                                        </Badge>
                                    </HStack>

                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Submitted On:
                                        </Text>
                                        <Text color="gray.800">
                                            {moment(registrationData.created_at).format("DD MMM YYYY, h:mm A")}
                                        </Text>
                                    </HStack>
                                </VStack>
                            </Box>

                            <Text fontSize="md" color="gray.600" textAlign="center">
                                We've received your course interest registration. Our team will review the course demand and confirm the course run. 
                                You'll receive an email notification once the course is confirmed with payment instructions.
                            </Text>

                            <VStack spacing={4} w="100%">
                                <Button
                                    colorScheme="teal"
                                    size="lg"
                                    onClick={() => navigate("/")}
                                    w="100%"
                                >
                                    Return to Home
                                </Button>
                                
                                <Button
                                    variant="outline"
                                    leftIcon={<ArrowBackIcon />}
                                    onClick={() => navigate("/registration")}
                                    w="100%"
                                >
                                    Register for Another Course
                                </Button>
                            </VStack>
                        </VStack>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
}; 